import { formatAmount, formatConfidence, formatDate } from '@shared/lib/format';
import { ActionsMenu } from '@shared/ui/menu/ActionsMenu';
import type { ActionItem } from '@shared/ui/menu/ActionsMenu';
import { ReceiptStatusBadge } from './ReceiptStatusBadge';
import type { Receipt } from '../types';

export interface ReceiptsTableProps {
  receipts: Receipt[];
  /** Appelé pour afficher les détails d'un ticket. */
  onView: (receipt: Receipt) => void;
  /** Appelé pour valider / remettre en revue un ticket. */
  onToggleStatus: (receipt: Receipt) => void;
  /** Appelé pour supprimer un ticket. */
  onDelete: (receipt: Receipt) => void;
  /** Ids des tickets en cours de traitement (désactive leurs actions). */
  pendingIds?: ReadonlySet<string>;
}

const confidenceTone = (confidence: number): string =>
  confidence >= 0.85 ? 'text-emerald-700' : confidence >= 0.6 ? 'text-amber-700' : 'text-red-700';

/**
 * Table moderne des tickets validés.
 * La visibilité des colonnes s'adapte à la largeur disponible.
 */
export function ReceiptsTable({
  receipts,
  onView,
  onToggleStatus,
  onDelete,
  pendingIds,
}: ReceiptsTableProps) {
  return (
    <div
      role="region"
      aria-label="Liste des tickets"
      tabIndex={0}
      className="min-w-0 overflow-x-auto rounded-lg border border-slate-200 bg-white"
    >
      <table className="w-full min-w-full text-left text-sm sm:min-w-[40rem]">
        <thead>
          <tr className="border-b border-slate-200 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            <th scope="col" className="px-5 py-3 font-semibold">
              Commerçant
            </th>
            <th scope="col" className="hidden px-5 py-3 font-semibold sm:table-cell">
              Date
            </th>
            <th scope="col" className="hidden px-5 py-3 font-semibold md:table-cell">
              Catégorie
            </th>
            <th scope="col" className="px-5 py-3 text-right font-semibold">
              Montant
            </th>
            <th scope="col" className="hidden px-5 py-3 font-semibold sm:table-cell">
              Confiance
            </th>
            <th scope="col" className="px-5 py-3 font-semibold">
              Statut
            </th>
            <th scope="col" className="px-5 py-3 text-right font-semibold">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {receipts.map((receipt) => (
            <tr
              key={receipt.id}
              data-testid="receipt-row"
              className="transition-colors hover:bg-slate-50"
            >
              <td className="px-5 py-3.5">
                <span className="font-medium text-slate-900">{receipt.merchant}</span>
                <span className="block text-xs text-slate-400">{receipt.fileName}</span>
                <span className="mt-0.5 block text-xs text-slate-500 sm:hidden">
                  {formatDate(receipt.date)}
                </span>
              </td>
              <td className="hidden px-5 py-3.5 whitespace-nowrap text-slate-600 sm:table-cell">
                {formatDate(receipt.date)}
              </td>
              <td className="hidden px-5 py-3.5 text-slate-600 md:table-cell">
                {receipt.category}
              </td>
              <td className="px-5 py-3.5 text-right font-medium whitespace-nowrap text-slate-900">
                {formatAmount(receipt.total, receipt.currency)}
              </td>
              <td
                className={`hidden px-5 py-3.5 font-medium sm:table-cell ${confidenceTone(receipt.confidence)}`}
              >
                {formatConfidence(receipt.confidence)}
              </td>
              <td className="px-5 py-3.5">
                <ReceiptStatusBadge status={receipt.status} />
              </td>
              <td className="px-5 py-3.5 text-right">
                <ReceiptActions
                  receipt={receipt}
                  pending={pendingIds?.has(receipt.id) ?? false}
                  onView={onView}
                  onToggleStatus={onToggleStatus}
                  onDelete={onDelete}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Actions d'une ligne ---------- */

interface ReceiptActionsProps {
  receipt: Receipt;
  pending: boolean;
  onView: (receipt: Receipt) => void;
  onToggleStatus: (receipt: Receipt) => void;
  onDelete: (receipt: Receipt) => void;
}

/** Menu d'actions d'un ticket : détails, valider / remettre en revue, supprimer. */
function ReceiptActions({
  receipt,
  pending,
  onView,
  onToggleStatus,
  onDelete,
}: ReceiptActionsProps) {
  const isValidated = receipt.status === 'validated';

  const items: ActionItem[] = [
    {
      label: 'Voir les détails',
      icon: 'eye',
      onSelect: () => onView(receipt),
      disabled: pending,
    },
    {
      label: isValidated ? 'Remettre en revue' : 'Valider le ticket',
      icon: 'check-circle',
      onSelect: () => onToggleStatus(receipt),
      disabled: pending,
    },
    {
      label: 'Supprimer',
      icon: 'trash',
      onSelect: () => onDelete(receipt),
      danger: true,
      disabled: pending,
    },
  ];

  return <ActionsMenu triggerLabel={`Actions pour ${receipt.merchant}`} items={items} />;
}
